import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import Breadcrumb from '../components/Breadcrumb';
import { 
  Users, UserPlus, Edit2, Trash2, Shield, 
  Mail, Phone, Calendar, Search, X, Check,
  ChevronDown, AlertCircle
} from 'lucide-react';

const ROLE_LABELS = {
  ORG_OWNER: { label: 'Kurum Sahibi', color: 'bg-purple-100 text-purple-700' },
  ORG_COORDINATOR: { label: 'Koordinatör', color: 'bg-blue-100 text-blue-700' },
  TEACHER: { label: 'Öğretmen', color: 'bg-green-100 text-green-700' },
  PARENT: { label: 'Veli', color: 'bg-yellow-100 text-yellow-700' },
  VIEW_ONLY: { label: 'Görüntüleyici', color: 'bg-gray-100 text-gray-700' },
};

const OrganizationMembers = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, [orgId]);

  useEffect(() => {
    if (organization) {
      document.title = `${organization.name} - Kullanıcılar | İlkadım360 Hub`;
    }
  }, [organization]);

  const loadData = async () => {
    try {
      const [orgRes, membersRes, statsRes] = await Promise.all([
        apiClient.get(`/organizations/${orgId}`),
        apiClient.get(`/org/${orgId}/members`),
        apiClient.get(`/org/${orgId}/members/stats`)
      ]);
      setOrganization(orgRes.data);
      setMembers(membersRes.data.members || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Veri yüklenemedi:', error);
      setError('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (formData) => {
    try {
      await apiClient.post(`/org/${orgId}/members`, formData);
      setShowAddModal(false);
      loadData();
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Kullanıcı eklenemedi');
    }
  };

  const handleUpdateMember = async (userId, updateData) => {
    try {
      await apiClient.put(`/org/${orgId}/members/${userId}`, updateData);
      setEditingMember(null);
      loadData();
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Kullanıcı güncellenemedi');
    }
  };

  const handleRemoveMember = async (userId, userName) => {
    if (!window.confirm(`${userName} kullanıcısını okuldan çıkarmak istediğinize emin misiniz?`)) {
      return;
    }
    try {
      await apiClient.delete(`/org/${orgId}/members/${userId}`);
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Kullanıcı silinemedi');
    }
  };

  const filteredMembers = members.filter(m => 
    m.user.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="org-members-page">
      <Breadcrumb 
        items={[
          { label: 'Okullar', path: '/organizations' },
          { label: organization?.name || 'Okul', path: `/org/${orgId}` },
          { label: 'Kullanıcılar', path: null }
        ]} 
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{organization?.name}</h1>
          <p className="text-gray-600">Kurum kullanıcıları ve rol yönetimi</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          data-testid="add-member-btn"
        >
          <UserPlus size={20} />
          Kullanıcı Ekle
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.totalMembers}</p>
            <p className="text-sm text-gray-500">Toplam Üye</p>
          </div>
          {stats.roleDistribution?.map((rd, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow-sm border p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{rd.count}</p>
              <p className="text-sm text-gray-500">{ROLE_LABELS[rd.role]?.label || rd.role}</p>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="İsim veya email ile ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="search-members"
          />
        </div>
      </div>

      {/* Members List */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kullanıcı</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İletişim</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kayıt Tarihi</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredMembers.map((member) => {
                const displayName = member.user.fullName || `${member.user.firstName || ''} ${member.user.lastName || ''}`.trim() || member.user.email;
                return (
                <tr key={member.user.id} className="hover:bg-gray-50" data-testid={`member-row-${member.user.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-600 font-medium">
                          {displayName.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{displayName}</p>
                        <p className="text-sm text-gray-500">{member.user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Mail size={14} />
                        <span>{member.user.email}</span>
                      </div>
                      {member.user.phone && (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Phone size={14} />
                          <span>{member.user.phone}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {member.roles.map((r, idx) => (
                        <span 
                          key={idx}
                          className={`px-2 py-1 rounded-full text-xs font-medium ${ROLE_LABELS[r.role]?.color || 'bg-gray-100 text-gray-700'}`}
                        >
                          {ROLE_LABELS[r.role]?.label || r.role}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {member.roles[0]?.assignedAt 
                      ? new Date(member.roles[0].assignedAt).toLocaleDateString('tr-TR')
                      : '-'
                    }
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setEditingMember(member)}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Düzenle"
                        data-testid={`edit-member-${member.user.id}`}
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleRemoveMember(member.user.id, displayName)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Okuldan Çıkar"
                        data-testid={`remove-member-${member.user.id}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
              })}
              {filteredMembers.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    {searchQuery ? 'Arama sonucu bulunamadı' : 'Henüz kullanıcı eklenmemiş'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <AddMemberModal 
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddMember}
        />
      )}

      {/* Edit Member Modal */}
      {editingMember && (
        <EditMemberModal 
          member={editingMember}
          onClose={() => setEditingMember(null)}
          onSubmit={(data) => handleUpdateMember(editingMember.user.id, data)}
        />
      )}
    </div>
  );
};

// Add Member Modal
const AddMemberModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    role: 'TEACHER'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await onSubmit(formData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" data-testid="add-member-modal">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Yeni Kullanıcı Ekle</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm">{error}</span>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ad Soyad *</label>
            <input
              type="text"
              required
              value={formData.fullName}
              onChange={(e) => setFormData({...formData, fullName: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Örn: Ahmet Yılmaz"
              data-testid="input-fullname"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="ornek@email.com"
              data-testid="input-email"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="+90 5XX XXX XX XX"
              data-testid="input-phone"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Şifre *</label>
            <input
              type="password"
              required
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="En az 8 karakter, büyük/küçük harf ve rakam"
              data-testid="input-password"
            />
            <p className="text-xs text-gray-500 mt-1">En az 8 karakter, büyük harf, küçük harf ve rakam içermeli</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rol *</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({...formData, role: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="select-role"
            >
              <option value="ORG_OWNER">Kurum Sahibi / Müdür</option>
              <option value="ORG_COORDINATOR">Koordinatör</option>
              <option value="TEACHER">Öğretmen</option>
              <option value="PARENT">Veli</option>
              <option value="VIEW_ONLY">Görüntüleyici</option>
            </select>
          </div>
          
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              data-testid="submit-add-member"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              ) : (
                <>
                  <UserPlus size={18} />
                  Ekle
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Member Modal
const EditMemberModal = ({ member, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    fullName: member.user.fullName || '',
    phone: member.user.phone || '',
    role: member.roles[0]?.role || 'TEACHER',
    isActive: member.user.isActive !== false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await onSubmit(formData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" data-testid="edit-member-modal">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Kullanıcı Düzenle</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm">{error}</span>
            </div>
          )}
          
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Email</p>
            <p className="font-medium">{member.user.email}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ad Soyad</label>
            <input
              type="text"
              value={formData.fullName}
              onChange={(e) => setFormData({...formData, fullName: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({...formData, role: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="ORG_OWNER">Kurum Sahibi / Müdür</option>
              <option value="ORG_COORDINATOR">Koordinatör</option>
              <option value="TEACHER">Öğretmen</option>
              <option value="PARENT">Veli</option>
              <option value="VIEW_ONLY">Görüntüleyici</option>
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="isActive" className="text-sm text-gray-700">Aktif Kullanıcı</label>
          </div>
          
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              ) : (
                <>
                  <Check size={18} />
                  Kaydet
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrganizationMembers;
